import axios from 'axios';
import { 
  setUser, 
  clearUser, 
  setUserLoading, 
  setUserError,
  setUserPermissions,
  clearUserPermissions,
  setPermissionsLoading,
  setPermissionsError
} from '../Actions/user';

/**
 * Load current user data and permissions
 */
export const loadCurrentUser = () => async (dispatch, getState) => {
  dispatch(setUserLoading(true));
  
  try {
    // Get team slug from URL or state
    const teamSlug = getTeamSlug(getState());
    
    // Get current user data
    const userResponse = await axios.get(`/api/teams/${teamSlug}/me`);
    const userData = userResponse.data;
    
    dispatch(setUser(userData));
    
    // Load user permissions if user is authenticated
    if (userData && userData.id) {
      await dispatch(loadUserPermissions(userData.id));
    }
    
  } catch (error) {
    console.error('Error loading current user:', error);
    dispatch(setUserError(error.message || 'Failed to load user data'));
    
    // If unauthorized, clear user data
    if (error.response && error.response.status === 401) {
      dispatch(clearUser());
      dispatch(clearUserPermissions());
    }
  } finally {
    dispatch(setUserLoading(false));
  }
};

/**
 * Load user permissions
 */
export const loadUserPermissions = (userId) => async (dispatch, getState) => {
  if (!userId) return;
  
  dispatch(setPermissionsLoading(true));
  
  try {
    // Get team slug from URL or state
    const teamSlug = getTeamSlug(getState());
    
    const response = await axios.get(`/api/teams/${teamSlug}/users/${userId}/permissions`);
    const permissionsData = response.data;
    
    // Extract just the permission tokens for easy checking
    const permissions = permissionsData.effective_permissions || [];
    
    dispatch(setUserPermissions(permissions));
    
  } catch (error) {
    console.error('Error loading user permissions:', error);
    dispatch(setPermissionsError(error.message || 'Failed to load user permissions'));
    
    // Don't fail completely if permissions can't be loaded
    // Set empty permissions array as fallback
    dispatch(setUserPermissions([]));
  } finally {
    dispatch(setPermissionsLoading(false));
  }
};

/**
 * Clear user data and permissions (for logout)
 */
export const logoutUser = () => (dispatch) => {
  dispatch(clearUser());
  dispatch(clearUserPermissions());
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (permissionToken) => (dispatch, getState) => {
  const state = getState();
  const userPermissions = (state.user && state.user.permissions) || [];
  
  return userPermissions.includes(permissionToken);
};

/**
 * Get team slug from URL or state
 */
const getTeamSlug = (state) => {
  // Try to get from state first
  if (state.team && state.team.slug) {
    return state.team.slug;
  }
  
  // Fallback to URL parsing
  const pathParts = window.location.pathname.split('/');
  if (pathParts[1] === 'app' && pathParts[2] === 'teams' && pathParts[3]) {
    return pathParts[3];
  }
  
  // Default fallback
  return 'default';
};

/**
 * Refresh user permissions (useful after permission changes)
 */
export const refreshUserPermissions = () => async (dispatch, getState) => {
  const state = getState();
  const userId = (state.user && state.user.data && state.user.data.id) || null;
  
  if (userId) {
    await dispatch(loadUserPermissions(userId));
  }
};
